import React, { useEffect, useMemo, useState } from 'react';
import './FrostGlassLoader.css';

const PROPOSAL_STATUSES = [
  { until: 17, text: 'Loading finish photography and gallery assets…', code: 'LDG / IMG' },
  { until: 34, text: 'Pulling plan drawings and elevation references…', code: 'LDG / DWG' },
  { until: 51, text: 'Applying visual standard and finish direction…', code: 'LDG / FIN' },
  { until: 68, text: 'Assembling proposal layout and typography…', code: 'LDG / ASM' },
  { until: 85, text: 'Warming up interactive views…', code: 'LDG / UI' },
  { until: 101, text: 'Opening your ADU proposal…', code: 'LDG / RDY' },
];

const GLOBAL_STATUSES = [
  { text: 'Securely processing your request…', code: 'SYS / PRC' },
  { text: 'Validating form data…', code: 'SYS / VAL' },
  { text: 'Connecting to Construx services…', code: 'SYS / NET' },
  { text: 'Almost there — finishing up…', code: 'SYS / FIN' },
];

function resolveProposalStatus(progress) {
  const step = PROPOSAL_STATUSES.find(s => progress < s.until) || PROPOSAL_STATUSES[PROPOSAL_STATUSES.length - 1];
  return step;
}

export default function FrostGlassLoader({
  visible,
  variant = 'default',
  label,
  hint,
  progress,
  statusMessages,
  phaseCode,
  panelMode = 'loading',
  panelKey = 0,
  progressNote,
  children,
}) {
  const [mounted, setMounted] = useState(visible);
  const [exiting, setExiting] = useState(false);
  const [entered, setEntered] = useState(false);
  const [cycleIndex, setCycleIndex] = useState(0);

  const indeterminate = progress == null && panelMode === 'loading';
  const clamped = indeterminate ? 0 : Math.min(100, Math.max(0, progress ?? 0));

  const copy = useMemo(() => {
    if (variant === 'proposal') {
      return {
        label: label || 'Preparing your ADU proposal',
        hint: hint || 'Client Proposal · Construx Backyard',
        phase: 'REF / LDG-01',
      };
    }
    if (variant === 'global') {
      return {
        label: label || 'Processing your request',
        hint: hint || 'Construx Backyard System',
        phase: 'SYS / REQ-01',
      };
    }
    return {
      label: label || 'Loading',
      hint: hint || 'Construx',
      phase: 'SYS / LDG',
    };
  }, [variant, label, hint]);

  const status = useMemo(() => {
    if (statusMessages?.length) {
      const idx = indeterminate
        ? cycleIndex % statusMessages.length
        : Math.min(statusMessages.length - 1, Math.floor((clamped / 100) * statusMessages.length));
      const item = statusMessages[idx];
      return typeof item === 'string' ? { text: item, code: copy.phase } : item;
    }
    if (variant === 'proposal' && !indeterminate) {
      return resolveProposalStatus(clamped);
    }
    if (variant === 'global' && indeterminate) {
      return GLOBAL_STATUSES[cycleIndex % GLOBAL_STATUSES.length];
    }
    if (indeterminate) {
      return { text: 'Loading…', code: copy.phase };
    }
    return { text: 'Loading assets…', code: copy.phase };
  }, [statusMessages, indeterminate, cycleIndex, clamped, variant, copy.phase]);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setExiting(false);
      setEntered(false);
      setCycleIndex(0);
      const enterTimer = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setEntered(true));
      });
      return () => window.cancelAnimationFrame(enterTimer);
    }

    if (!mounted) return undefined;

    setExiting(true);
    setEntered(false);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setExiting(false);
    }, 720);

    return () => window.clearTimeout(timer);
  }, [visible, mounted]);

  useEffect(() => {
    if (!mounted || !indeterminate) return undefined;
    const interval = window.setInterval(() => {
      setCycleIndex(i => i + 1);
    }, 2400);
    return () => window.clearInterval(interval);
  }, [mounted, indeterminate, variant]);

  if (!mounted) return null;

  const showPanel = panelMode !== 'hidden';
  const busyLabel = panelMode === 'verify'
    ? 'Email verification required'
    : indeterminate && label
      ? `${label}. ${status.text}`
      : `${copy.label}. ${status.text}`;

  return (
    <div
      className={`frost-loader${exiting ? ' is-exiting' : ''}${entered ? ' is-entered' : ''}`}
      role="status"
      aria-live="polite"
      aria-busy={!exiting && (panelMode === 'loading' || panelMode === 'verify')}
      aria-label={busyLabel}
    >
      <div className="frost-loader__veil" aria-hidden="true" />
      <div className="frost-loader__orb frost-loader__orb--a" aria-hidden="true" />
      <div className="frost-loader__orb frost-loader__orb--b" aria-hidden="true" />

      {showPanel ? (
      <div className="frost-loader__panel">
        <div
          key={panelKey}
          className={`frost-loader__panel-body${panelMode === 'verify' ? ' is-verify' : ''}`}
        >
          {panelMode === 'verify' && children ? (
            children
          ) : (
            <>
              <div className="frost-loader__header frost-loader__stagger" style={{ '--stagger': 0 }}>
                <span className="frost-loader__hint">{copy.hint}</span>
                <span className="frost-loader__phase">{status.code || phaseCode || copy.phase}</span>
              </div>

              <h2 className="frost-loader__label frost-loader__stagger" style={{ '--stagger': 1 }}>
                {label || copy.label}
              </h2>

              <p
                key={`${status.text}-${status.code}`}
                className="frost-loader__status frost-loader__stagger"
                style={{ '--stagger': 2 }}
              >
                {status.text}
              </p>

              <div className="frost-loader__progress frost-loader__stagger" style={{ '--stagger': 3 }}>
                <div className="frost-loader__track" aria-hidden="true">
                  <div
                    className={`frost-loader__fill${indeterminate ? ' is-indeterminate' : ''}`}
                    style={indeterminate ? undefined : { width: `${clamped}%` }}
                  >
                    {clamped > 2 ? <span className="frost-loader__fill-tip" aria-hidden="true" /> : null}
                  </div>
                </div>
                <div className="frost-loader__foot">
                  {indeterminate ? (
                    <span className="frost-loader__pulse" aria-hidden="true">
                      <i /><i /><i />
                    </span>
                  ) : (
                    <span className="frost-loader__pct">{clamped}%</span>
                  )}
                  <span className="frost-loader__foot-note">
                    {indeterminate
                      ? (progressNote || 'Stand by')
                      : clamped >= 100
                        ? 'Ready'
                        : 'Loading'}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      ) : null}
    </div>
  );
}
